export type CookiePreferences = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
};

export const COOKIE_PREFERENCES_KEY = 'cookie-preferences';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  updatedAt: '',
};

const parsePreferences = (stored: string | null) => {
  if (!stored) {
    return null;
  }
  try {
    const parsed = JSON.parse(stored) as Partial<CookiePreferences>;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') {
      return null;
    }
    return {
      necessary: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      updatedAt: parsed.updatedAt ?? '',
    };
  } catch {
    return null;
  }
};

const getCookieValue = (name: string) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const cookies = document.cookie.split(';').map((item) => item.trim());
  const match = cookies.find((item) => item.startsWith(`${name}=`));
  if (!match) {
    return null;
  }
  return decodeURIComponent(match.split('=').slice(1).join('='));
};

const setCookieValue = (name: string, value: string) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
};

export const getCookiePreferences = (): CookiePreferences | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = parsePreferences(window.localStorage.getItem(COOKIE_PREFERENCES_KEY));
  if (stored) {
    return stored;
  }

  const cookieStored = parsePreferences(getCookieValue(COOKIE_PREFERENCES_KEY));
  if (cookieStored) {
    window.localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(cookieStored));
    return cookieStored;
  }

  return null;
};

export const saveCookiePreferences = (preferences: CookiePreferences) => {
  if (typeof window === 'undefined') {
    return;
  }
  const serialized = JSON.stringify(preferences);
  window.localStorage.setItem(COOKIE_PREFERENCES_KEY, serialized);
  setCookieValue(COOKIE_PREFERENCES_KEY, serialized);
};
